import { Injectable, Type } from '@angular/core';
import {
  CreateDataFrameRawParameters,
  CubeComment,
  CubeEditResult,
  CubeWorkflowData,
  EnrichmentToolBatchRawParameters,
  EnrichmentToolRawParameters,
  ExternalSqlQueryRawParameters,
  GraphNodeOptions,
  ReadDataFrameRawParameters,
  WorkflowExpressionData,
  WorkflowJsonToColumnTransformationData,
  WorkflowRunnerLegacyData,
  WorkflowTriggerData,
  WriteDataFrameRawParameters,
  WriteDataFrameTransitRawParameters,
} from '@selfai-platform/pipeline-common';
import { DialogService } from '@selfai-platform/shell';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { EMPTY, Observable } from 'rxjs';
import {
  CmsFormCreatorComponent,
  CodeEditorTransformationComponent,
  CommentComponent,
  CreateTriggerComponent,
  ExpressionToolComponent,
  FavoriteCubeAddComponent,
  JsonToColumnTransformationComponent,
  ReadDataFrameComponent,
  WriteDataFrameComponent,
  WriteDataFrameTransitComponent,
} from '../components';
import { CreateDataFrameContainerComponent } from '../components/dialogs/create-data-frame';
import { EnrichmentToolComponent } from '../components/dialogs/enrichment-tool';
import { EnrichmentToolBatchComponent } from '../components/dialogs/enrichment-tool-batch';
import { ExternalSqlQueryComponent } from '../components/dialogs/external-sql-query';
import { WorkflowRunnerComponent } from '../components/dialogs/workflow-runner/workflow-runner.component';
import { CubeDialogManagementService } from './cube-dialog-management.service';

@Injectable()
export class CubeDialogService {
  public readonly defaultDialogOptions: Pick<DynamicDialogConfig, 'width' | 'height'> = {
    width: '65vw',
    height: '80vh',
  };

  constructor(
    private readonly dialogService: DialogService<CubeEditResult, CubeWorkflowData | CubeComment>,
    private readonly favoriteDialogService: DialogService<never, GraphNodeOptions>,
    private readonly dialogManagementService: CubeDialogManagementService,
  ) {}

  showCreateTrigger(data: CubeWorkflowData<WorkflowTriggerData>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(CreateTriggerComponent, {
      data,
      modal: false,
      draggable: true,
      resizable: true,
      closeOnEscape: false,
      showHeader: true,
      maximizable: true,
      duplicate: true,
    });
  }

  showReadDataFrame(data: CubeWorkflowData<ReadDataFrameRawParameters>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(ReadDataFrameComponent, {
      data,
      modal: false,
      draggable: true,
      resizable: true,
      maximizable: true,
      autoZIndex: true,
      closeOnEscape: false,
      showHeader: true,
      duplicate: true,
    });
  }

  showWriteDataFrameTransit(data: CubeWorkflowData<WriteDataFrameTransitRawParameters>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(WriteDataFrameTransitComponent, {
      data,
      modal: false,
      draggable: true,
      maximizable: true,
      resizable: true,
      autoZIndex: true,
      closeOnEscape: false,
      showHeader: true,
      duplicate: true,
    });
  }

  showWriteDataFrame(data: CubeWorkflowData<WriteDataFrameRawParameters>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(WriteDataFrameComponent, {
      data,
      modal: false,
      draggable: true,
      resizable: true,
      maximizable: true,
      closeOnEscape: false,
      showHeader: true,
      duplicate: true,
    });
  }

  showCreateDataFrame(data: CubeWorkflowData<CreateDataFrameRawParameters>): Observable<CubeEditResult> {
    return this.showCubeDialog(CreateDataFrameContainerComponent, {
      data,
      modal: false,
      draggable: true,
      maximizable: true,
      closeOnEscape: false,
      showHeader: false,
    });
  }

  showExternalSqlQuery(data: CubeWorkflowData<ExternalSqlQueryRawParameters>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(ExternalSqlQueryComponent, {
      data,
      modal: false,
      draggable: true,
      maximizable: true,
      closeOnEscape: false,
      showHeader: true,
      resizable: true,
      duplicate: true,
    });
  }

  showEnrichmentTool(data: CubeWorkflowData<EnrichmentToolRawParameters>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(EnrichmentToolComponent, {
      data,
      maximizable: true,
      closeOnEscape: false,
      showHeader: true,
      modal: false,
      draggable: true,
      resizable: true,
      duplicate: true,
    });
  }

  showEnrichmentToolBatch(data: CubeWorkflowData<EnrichmentToolBatchRawParameters>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(EnrichmentToolBatchComponent, {
      data,
      modal: false,
      draggable: true,
      maximizable: true,
      closeOnEscape: false,
      showHeader: true,
      resizable: true,
      duplicate: true,
    });
  }

  showJsonToColumnTransformation(
    data: CubeWorkflowData<WorkflowJsonToColumnTransformationData>,
  ): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(JsonToColumnTransformationComponent, {
      data,
      modal: false,
      draggable: true,
      closeOnEscape: false,
      showHeader: true,
      maximizable: true,
      resizable: true,
      duplicate: true,
    });
  }

  showWorkflowRunner(data: CubeWorkflowData<WorkflowRunnerLegacyData>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(WorkflowRunnerComponent, {
      data,
      modal: false,
      draggable: true,
      closeOnEscape: false,
      showHeader: true,
      width: '75%',
      maximizable: true,
      resizable: true,
      duplicate: true,
      focusOnShow: false,
    });
  }

  showComment(data: CubeComment): Observable<CubeEditResult> {
    return this.dialogService.showDialog(CommentComponent, {
      header: 'Comment',
      data,
    });
  }

  showExpressionTool(data: CubeWorkflowData<WorkflowExpressionData>): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(ExpressionToolComponent, {
      data,
      closeOnEscape: false,
      showHeader: true,
      maximizable: true,
      modal: false,
      draggable: true,
      resizable: true,
      duplicate: true,
    });
  }

  showCodeEditorTransformation(data: CubeWorkflowData): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(CodeEditorTransformationComponent, {
      data,
      modal: false,
      closeOnEscape: false,
      showHeader: true,
      maximizable: true,
      draggable: true,
      resizable: true,
      duplicate: true,
    });
  }

  showCmsFormCreator(data: CubeWorkflowData): Observable<CubeEditResult> {
    if (this.dialogManagementService.isExist(data.selectedNode.id)) {
      this.dialogManagementService.expand(data.selectedNode.id);

      return EMPTY;
    }

    return this.showCubeDialog(CmsFormCreatorComponent, {
      data,
      closeOnEscape: false,
      showHeader: true,
      maximizable: true,
      draggable: true,
      resizable: true,
      duplicate: true,
      modal: false,
    });
  }

  showFavoriteAddForm(data: GraphNodeOptions): Observable<void> {
    return this.favoriteDialogService.showDialog(FavoriteCubeAddComponent, {
      data,
      width: '40%',
      header: 'Add to favorites',
    });
  }

  private showCubeDialog<T extends object>(
    component: Type<T>,
    options: DynamicDialogConfig = {},
  ): Observable<CubeEditResult> {
    return this.dialogService.showDialog(component, {
      ...this.defaultDialogOptions,
      ...(options || {}),
    });
  }
}
